import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, Play, SkipForward, X, User, Briefcase, Phone } from 'lucide-react';
import { Button } from '@/modules/shared/components/ui/button';
import { Badge } from '@/modules/shared/components/ui/badge';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import {
  isDemoModeEnabled,
  getDemoFlowForRole,
  type DemoFlow,
} from '@/modules/shared/services/demoFlowService';
import { DemoGuideOverlay } from './DemoGuideOverlay';

// ─── ONBOARDING STEP DEFINITIONS ───────────────────────

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const CC_ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'create-patient',
    title: 'Create a Patient',
    description: 'Enroll your first patient using the dynamic registration form. Fill in demographics, diagnosis, and contact details.',
    icon: User,
  },
  {
    id: 'create-case',
    title: 'Create an Assistance Case',
    description: 'Link a patient to a financial assistance plan. The case form captures enrollment reason and priority level.',
    icon: Briefcase,
  },
  {
    id: 'add-call-log',
    title: 'Add a Call Log',
    description: 'Document patient interactions. Call logs track type (inbound/outbound), status, notes, and follow-up dates.',
    icon: Phone,
  },
];

const CCM_ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'review-case',
    title: 'Review a Pending Case',
    description: 'Open the approval queue and review a case. Check patient history, treatment plan, and financial details.',
    icon: Briefcase,
  },
  {
    id: 'approve-case',
    title: 'Approve or Reject a Case',
    description: 'Make a decision on a pending case. Comments are required for all decisions to maintain an audit trail.',
    icon: CheckCircle,
  },
  {
    id: 'view-dashboard',
    title: 'View the Dashboard',
    description: 'Check program-wide KPIs: case distributions, approval turnaround, and utilization trends.',
    icon: Play,
  },
];

// ─── PERSISTENCE ────────────────────────────────────────

const ONBOARDING_KEY_PREFIX = 'epprem_onboarding_completed:';

function isOnboardingCompleted(userId: string): boolean {
  return localStorage.getItem(ONBOARDING_KEY_PREFIX + userId) === 'true';
}

function markOnboardingCompleted(userId: string): void {
  localStorage.setItem(ONBOARDING_KEY_PREFIX + userId, 'true');
}

// ─── HOOK: useOnboardingCheck ───────────────────────────

export function useOnboardingCheck(): { shouldShowOnboarding: boolean } {
  const { user } = useAuth();

  if (!isDemoModeEnabled() || !user) return { shouldShowOnboarding: false };
  if (isOnboardingCompleted(user.userId)) return { shouldShowOnboarding: false };

  // Show onboarding if user has never logged in before OR is a demo user
  const isDemoUser = user.email?.endsWith('@demo.com') ?? false;
  const isFirstLogin = !user.lastLoginDate;

  return { shouldShowOnboarding: isDemoUser || isFirstLogin };
}

// ─── MAIN COMPONENT ─────────────────────────────────────

export function OnboardingFlow() {
  const { user } = useAuth();
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'welcome' | 'steps' | 'guide' | 'dismissed'>('welcome');
  const [demoFlow, setDemoFlow] = useState<DemoFlow | null>(null);

  const roleName = user?.programRoleName ?? user?.role ?? '';
  const isCC = roleName.toLowerCase().includes('care coordinator') && !roleName.toLowerCase().includes('manager');
  const steps = isCC ? CC_ONBOARDING_STEPS : CCM_ONBOARDING_STEPS;

  // ─── Auto-detect flow ────────────────────────────────
  useEffect(() => {
    if (roleName) {
      const flow = getDemoFlowForRole(roleName);
      setDemoFlow(flow);
    }
  }, [roleName]);

  const handleSkip = useCallback(() => {
    if (user) markOnboardingCompleted(user.userId);
    setPhase('dismissed');
  }, [user]);

  const handleStartGuide = useCallback(() => {
    setPhase('guide');
  }, []);

  const handleGuideClose = useCallback(() => {
    if (user) markOnboardingCompleted(user.userId);
    setPhase('dismissed');
  }, [user]);

  const handleExplore = useCallback(() => {
    if (user) markOnboardingCompleted(user.userId);
    setPhase('dismissed');
    if (programId) navigate(`/programs/${programId}/workspace`);
  }, [user, programId, navigate]);

  if (phase === 'dismissed') return null;

  // ─── Guide overlay ───────────────────────────────────
  if (phase === 'guide' && demoFlow && programId) {
    return (
      <DemoGuideOverlay
        flow={demoFlow}
        programId={programId}
        onClose={handleGuideClose}
      />
    );
  }

  // ─── Welcome screen ──────────────────────────────────
  if (phase === 'welcome') {
    return (
      <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/50">
        <div className="w-[480px] max-w-[92vw] rounded-lg border border-border bg-background p-6 shadow-2xl">
          {/* Close */}
          <div className="flex justify-end">
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleSkip}>
              <X className="h-3 w-3" />
            </Button>
          </div>

          {/* Header */}
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Play className="h-5 w-5 text-primary" />
            </div>
            <h2 className="mt-3 text-[16px] font-semibold text-foreground">
              Welcome, {user?.firstName ?? 'User'}!
            </h2>
            <p className="mt-1 text-[12px] text-muted-foreground">
              You're signed in as{' '}
              <Badge variant="outline" className="text-[11px]">{roleName || user?.role}</Badge>
            </p>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {isCC
                ? 'As a Care Coordinator, you enroll patients, create assistance cases, and log calls.'
                : 'As a Care Case Manager, you review and approve cases, monitor dashboards, and track KPIs.'}
            </p>
          </div>

          {/* Action buttons */}
          <div className="mt-5 flex flex-col gap-2">
            <Button className="h-9 w-full text-[12px]" onClick={() => setPhase('steps')}>
              Show me around
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" className="h-9 w-full text-[12px]" onClick={handleExplore}>
              I'll explore on my own
              <SkipForward className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Steps overview ──────────────────────────────────
  if (phase === 'steps') {
    return (
      <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/50">
        <div className="w-[520px] max-w-[92vw] rounded-lg border border-border bg-background p-6 shadow-2xl">
          {/* Close */}
          <div className="flex justify-end">
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleSkip}>
              <X className="h-3 w-3" />
            </Button>
          </div>

          {/* Header */}
          <h2 className="text-[15px] font-semibold text-foreground">
            {isCC ? 'Care Coordinator Quick Start' : 'Case Manager Quick Start'}
          </h2>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Here's what you'll learn in the guided walkthrough:
          </p>

          {/* Steps */}
          <div className="mt-4 space-y-3">
            {steps.map((s, idx) => (
              <div
                key={s.id}
                className="flex items-start gap-3 rounded-md border border-border bg-muted/20 p-3"
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                  {idx + 1}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <s.icon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-[12px] font-medium text-foreground">{s.title}</span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{s.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="mt-5 flex items-center justify-between">
            <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={handleSkip}>
              Skip for now
            </Button>
            <Button size="sm" className="h-8 text-[12px]" onClick={handleStartGuide}>
              Start guided tour
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
